import { useState, useEffect, useRef, useCallback } from 'react';
import { getMissedNotifications } from '../api/apiHelper';

interface UseMissedNotificationsPollingOptions {
  userId: number | null;
  enabled: boolean;
  onNotificationsReceived: (notifications: any[]) => void;
  pollingInterval?: number;
}

interface PollingStatus {
  isLeader: boolean;
  lastCheckTime: string | null;
}

const HEARTBEAT_INTERVAL = 120000;
const STALE_THRESHOLD = 360000;
const LEADER_CHECK_INTERVAL = 120000;
const DEFAULT_POLLING_INTERVAL = 3600000;

function getStorageKey(userId: number, type: 'leader' | 'lastCheck'): string {
  if (type === 'leader') {
    return `missed_notifications_polling_leader_${userId}`;
  }
  return `missed_notifications_last_check_${userId}`;
}

function isLeaderStale(userId: number): boolean {
  try {
    const leaderKey = getStorageKey(userId, 'leader');
    const leaderHeartbeat = localStorage.getItem(leaderKey);

    if (!leaderHeartbeat) {
      return true;
    }

    const heartbeatTime = new Date(leaderHeartbeat).getTime();
    const now = Date.now();
    const timeSinceHeartbeat = now - heartbeatTime;

    return timeSinceHeartbeat > STALE_THRESHOLD;
  } catch (error) {
    return true;
  }
}

function writeHeartbeat(userId: number): void {
  try {
    const leaderKey = getStorageKey(userId, 'leader');
    const now = new Date().toISOString();
    localStorage.setItem(leaderKey, now);
  } catch (error) {
  }
}

function getLastCheckTime(userId: number): string | null {
  try {
    const lastCheckKey = getStorageKey(userId, 'lastCheck');
    return localStorage.getItem(lastCheckKey);
  } catch (error) {
    return null;
  }
}

function setLastCheckTime(userId: number, timestamp: string): void {
  try {
    const lastCheckKey = getStorageKey(userId, 'lastCheck');
    localStorage.setItem(lastCheckKey, timestamp);
  } catch (error) {
  }
}

function clearLeaderLock(userId: number): void {
  try {
    const leaderKey = getStorageKey(userId, 'leader');
    localStorage.removeItem(leaderKey);
  } catch (error) {
  }
}

function getInitialPollTimestamp(userId: number): string {
  const lastCheck = getLastCheckTime(userId);
  if (lastCheck) {
    return lastCheck;
  }

  try {
    // Use the same key as NotificationContext to get the previous login time
    const previousLoginKey = `previousLoginTime_${userId}`;
    const previousLogin = localStorage.getItem(previousLoginKey);
    if (previousLogin) {
      return previousLogin;
    }
  } catch (error) {
  }

  const twentyFourHoursAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  return twentyFourHoursAgo;
}

export function useMissedNotificationsPolling(
  options: UseMissedNotificationsPollingOptions
): PollingStatus {
  const {
    userId,
    enabled,
    onNotificationsReceived,
    pollingInterval = DEFAULT_POLLING_INTERVAL,
  } = options;

  const [isLeader, setIsLeader] = useState(false);
  const [lastCheckTime, setLastCheckTimeState] = useState<string | null>(null);

  const heartbeatIntervalRef = useRef<NodeJS.Timer | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timer | null>(null);
  const leaderCheckIntervalRef = useRef<NodeJS.Timer | null>(null);

  const onNotificationsReceivedRef = useRef(onNotificationsReceived);
  useEffect(() => {
    onNotificationsReceivedRef.current = onNotificationsReceived;
  }, [onNotificationsReceived]);

  const executePoll = useCallback(async (currentUserId: number) => {
    try {
      const pollTimestamp = getLastCheckTime(currentUserId) || getInitialPollTimestamp(currentUserId);
      const notifications = await getMissedNotifications(pollTimestamp);

      if (notifications && Array.isArray(notifications) && notifications.length > 0) {
        onNotificationsReceivedRef.current(notifications);
      } else {
      }

      const now = new Date().toISOString();
      setLastCheckTime(currentUserId, now);
      setLastCheckTimeState(now);

    } catch (error: any) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        return false;
      }
    }

    return true;
  }, []);

  const resignLeadership = useCallback((currentUserId: number | null) => {
    setIsLeader(false);

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (currentUserId) {
      clearLeaderLock(currentUserId);
    }
  }, []);

  const becomeLeader = useCallback((currentUserId: number) => {
    if (!currentUserId) return;

    setIsLeader(true);

    writeHeartbeat(currentUserId);

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    heartbeatIntervalRef.current = setInterval(() => {
      writeHeartbeat(currentUserId);
    }, HEARTBEAT_INTERVAL);

    const lastCheck = getLastCheckTime(currentUserId);
    if (lastCheck) {
      const timeSinceLastCheck = Date.now() - new Date(lastCheck).getTime();
      if (timeSinceLastCheck > pollingInterval) {
        executePoll(currentUserId);
      }
    } else {
      executePoll(currentUserId);
    }

    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }
    pollingIntervalRef.current = setInterval(async () => {
      const shouldContinue = await executePoll(currentUserId);
      if (!shouldContinue) {
        resignLeadership(currentUserId);
      }
    }, pollingInterval);

    if (leaderCheckIntervalRef.current) {
      clearInterval(leaderCheckIntervalRef.current);
      leaderCheckIntervalRef.current = null;
    }
  }, [executePoll, pollingInterval, resignLeadership]);

  const becomeFollower = useCallback((currentUserId: number) => {
    if (!currentUserId) return;

    setIsLeader(false);

    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (leaderCheckIntervalRef.current) {
      clearInterval(leaderCheckIntervalRef.current);
    }
    leaderCheckIntervalRef.current = setInterval(() => {
      if (isLeaderStale(currentUserId)) {
        becomeLeader(currentUserId);
      }
    }, LEADER_CHECK_INTERVAL);
  }, [becomeLeader]);

  const attemptLeaderElection = useCallback((currentUserId: number) => {
    if (!currentUserId) return;

    if (isLeaderStale(currentUserId)) {
      becomeLeader(currentUserId);
    } else {
      becomeFollower(currentUserId);
    }
  }, [becomeLeader, becomeFollower]);

  const isLeaderRef = useRef(isLeader);
  useEffect(() => {
    isLeaderRef.current = isLeader;
  }, [isLeader]);

  useEffect(() => {
    if (!enabled || !userId) {
      resignLeadership(userId);
      return;
    }

    const storedLastCheck = getLastCheckTime(userId);
    if (storedLastCheck) {
      setLastCheckTimeState(storedLastCheck);
    }

    attemptLeaderElection(userId);

    const handleBeforeUnload = () => {
      if (isLeaderRef.current) {
        clearLeaderLock(userId);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      resignLeadership(userId);
    };
  }, [enabled, userId, attemptLeaderElection, resignLeadership]);

  return {
    isLeader,
    lastCheckTime,
  };
}
